import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardCheck, Lightbulb, CheckCircle, BarChart3, Sparkles, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useParticipantCount } from '../context/ParticipantContext';

const HeroSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { formatted: participantCount } = useParticipantCount();
  
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const fullText = t('heroTitle');
  const typingSpeed = 80;
  const deletingSpeed = 40;
  const pauseTime = 2000;

  useEffect(() => {
    // Reset when language changes
    setDisplayText('');
    setIsDeleting(false);
  }, [language]);

  useEffect(() => {
    let timeout;

    if (!isDeleting && displayText === fullText) {
      timeout = setTimeout(() => setIsDeleting(true), pauseTime);
    } else if (isDeleting && displayText === '') {
      timeout = setTimeout(() => setIsDeleting(false), 500);
    } else if (isDeleting) {
      timeout = setTimeout(() => {
        setDisplayText(fullText.substring(0, displayText.length - 1));
      }, deletingSpeed);
    } else {
      timeout = setTimeout(() => {
        setDisplayText(fullText.substring(0, displayText.length + 1));
      }, typingSpeed);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, fullText]);

  const badges = [
    { icon: CheckCircle, text: t('free'), color: 'text-green-400' },
    { icon: BarChart3, text: t('realTimeBenchmark'), color: 'text-blue-300' },
    { icon: Sparkles, text: t('aiPoweredInsights'), color: 'text-yellow-400' },
    { icon: Clock, text: t('fourMinutes'), color: 'text-cyan-400' },
  ];

  return (
    <section className="relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-400 to-blue-600" />
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center">
          {/* Title with Typewriter Effect */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 min-h-[1.2em]">
            {displayText}
            <span className="animate-pulse">|</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10">
            {t('heroSubtitle').replace('200+', participantCount)}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <Link to="/quickscan/start">
              <Button
                variant="outline"
                size="lg"
                className="bg-white text-blue-500 border-white hover:bg-white/90 hover:text-blue-600 px-8 py-6 text-base font-semibold rounded-full"
                data-testid="hero-start-assessment-btn"
              >
                <ClipboardCheck className="mr-2 h-5 w-5" />
                {t('startFreeAssessment')}
              </Button>
            </Link>
            {isAuthenticated && (
              <Link to="/use-cases">
                <Button
                  size="lg"
                  className="bg-slate-800/80 text-white hover:bg-slate-800 px-8 py-6 text-base font-semibold rounded-full"
                  data-testid="hero-explore-usecases-btn"
                >
                  <Lightbulb className="mr-2 h-5 w-5" />
                  {t('exploreUseCases')}
                </Button>
              </Link>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {badges.map((badge) => (
              <div key={badge.text} className="flex items-center space-x-2 text-white/90">
                <badge.icon className={`h-5 w-5 ${badge.color}`} />
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
